// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useEffect, useRef, useState } from 'react';
import { useDash } from '../context';
import { StackBars } from '../charts/StackBars';
import { BarList } from '../charts/BarList';
import { Card, ErrorLine, Empty, Note, Tag } from '../ui';
import { errorMessage } from '../api';
import type { RealtimeResponse, RealtimeFeedRow } from '../types';
import { ago } from '../format';

/** Realtime is polled every 10 s, only while this tab is mounted AND the document is visible. */
export const REALTIME_POLL_MS = 10_000;

const FEED_ICON: Record<string, string> = { pageview: '👁', leave: '↩', event: '•' };
const EVENT_LABEL: Record<string, string> = {
  product_view: 'Viewed product', add_to_cart: 'Added to cart', checkout_start: 'Started checkout', order: 'Placed order',
  pricing_view: 'Viewed pricing', signup_start: 'Started sign-up', account_created: 'Created an account', subscription: 'Subscribed', search: 'Searched',
};
const feedText = (r: RealtimeFeedRow) => (r.type === 'pageview' ? 'Viewed' : r.type === 'leave' ? 'Left' : EVENT_LABEL[r.name || ''] || r.name || 'Event');
const deviceIcon = (d: string) => (/mobile|tablet/i.test(d) ? '📱' : d ? '💻' : '');

export const RealtimeTab: React.FC = () => {
  const d = useDash();
  const [data, setData] = useState<RealtimeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const setLive = useRef(d.setLive);
  setLive.current = d.setLive;
  const report = useRef(d.reportLoading);
  report.current = d.reportLoading;

  useEffect(() => {
    let off = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // One chain only: a hide/show while a request is in flight must not start a second one.
    let inFlight = false;
    const clear = () => { if (timer) clearTimeout(timer); timer = undefined; };
    setData(null);
    setLoading(true);
    const tick = async () => {
      clear();
      if (off || inFlight) return;
      if (typeof document !== 'undefined' && document.hidden) return; // resumed by visibilitychange
      inFlight = true;
      try {
        const r = await d.api.realtime(d.siteId);
        if (off) return;
        setData(r); setError(null); setLive.current(r.live);
      } catch (e) {
        if (!off) setError(errorMessage(e));
      } finally {
        inFlight = false;
        if (!off) {
          setLoading(false);
          clear();
          if (!document.hidden) timer = setTimeout(tick, REALTIME_POLL_MS);
        }
      }
    };
    const onVis = () => {
      if (document.hidden) clear();
      else if (!timer && !inFlight) tick();
    };
    document.addEventListener('visibilitychange', onVis);
    tick();
    return () => { off = true; clear(); document.removeEventListener('visibilitychange', onVis); };
  }, [d.api, d.siteId]);

  useEffect(() => { report.current('realtime', loading); }, [loading]);
  useEffect(() => () => report.current('realtime', false), []);

  const where = d.siteId === '_all' ? 'all websites' : d.site?.name || d.siteId;
  return (
    <div>
      {error && <ErrorLine message={error} />}
      <div className="grid gap-3.5 grid-cols-1 lg:grid-cols-[2fr_1fr]">
        <Card title="Visitors right now">
          <div className="flex items-baseline gap-2.5">
            <div className="text-[44px] font-bold" data-testid="wa-rt-live">{data?.live ?? 0}</div>
            <span className="text-stone-500">on {where}</span>
          </div>
          <div className="text-[11px] text-stone-500 mt-2.5 mb-1.5">Page views per minute · last 30 minutes</div>
          <StackBars bottom={(data?.perMinute || new Array(30).fill(0)) as number[]} top={[]} height={140} bottomName="Page views" />
        </Card>
        <Card title="Active pages">
          <BarList rows={(data?.pages || []).map((p) => ({ key: p.path, label: p.path, value: p.count, onClick: d.rowFilter('page', p.path) }))} empty="Nobody on the site right now" />
        </Card>
      </div>
      <div className="grid gap-3.5 mt-3.5 grid-cols-1 md:grid-cols-2">
        <Card title={<>Live activity <Tag tone="green">● streaming</Tag></>}>
          <div className="max-h-[330px] overflow-auto">
            {!data?.feed.length ? <Empty>Nothing in the last 30 minutes</Empty> : data.feed.map((r, i) => (
              <div key={`${r.ts}-${i}`} className="flex gap-2.5 p-2 border-b border-stone-100 items-center">
                <span>{r.type === 'event' && r.name === 'order' ? '✅' : FEED_ICON[r.type] || '•'}</span>
                <span className="min-w-0 truncate"><b>{feedText(r)}</b> {r.path}</span>
                <small className="ml-auto text-stone-500 text-[11px] whitespace-nowrap">
                  {deviceIcon(r.device)} {r.city}{r.siteName ? ` · ${r.siteName}` : ''} · {ago(r.ts)}
                </small>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Where they are">
          <BarList rows={(data?.cities || []).map((c) => ({ key: c.city, label: c.city, value: c.count, onClick: d.rowFilter('city', c.city) }))} empty="Nobody on the site right now" />
          <Note>Locations come from the visitor's IP address. The address itself is never stored in the analytics data.</Note>
        </Card>
      </div>
    </div>
  );
};

export default RealtimeTab;
